import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://shipany.in";
export const OG_IMAGE = `${SITE_URL}/og-image.png`;

const PAGE_SEO = {
  "/": {
    title: "ShipAny — Domestic & International Shipping Aggregator India",
    description: "Compare courier rates, book domestic and international shipments, print labels, track deliveries, manage COD, NDR and returns from one Indian logistics platform.",
    keywords: "shipping aggregator India, domestic courier platform, international shipping India, courier rate comparison, COD shipping, bulk shipping, logistics SaaS",
  },
  "/features": {
    title: "Shipping Platform Features — Courier Rates, COD, NDR, Returns | ShipAny",
    description: "Explore ShipAny features for courier comparison, shipment booking, labels, tracking, COD reconciliation, NDR management, returns, analytics and shipping automation.",
    keywords: "shipping software features, courier management software, COD management, NDR management, shipment tracking platform",
  },
  "/pricing": {
    title: "Shipping Rates & Courier Price Calculator India | ShipAny",
    description: "Calculate domestic and international shipping rates from India. Compare courier services, chargeable weight, delivery estimates and transparent prices before booking.",
    keywords: "shipping rate calculator India, courier price calculator, domestic shipping rates, international courier rates India",
  },
  "/domestic-shipping": {
    title: "Domestic Shipping Services Across India | ShipAny",
    description: "Ship across India with multiple courier options, live rate comparison, pickup management, COD support, shipping labels, tracking, NDR and bulk shipping tools.",
    keywords: "domestic shipping India, courier aggregator India, COD shipping India, bulk shipping platform, ecommerce shipping India",
  },
  "/international-shipping": {
    title: "International Shipping from India for Exporters & Brands | ShipAny",
    description: "Ship internationally from India with customs-ready item details, chargeable-weight calculation, courier comparison, shipping documents and global tracking.",
    keywords: "international shipping India, export shipping India, international courier aggregator, ship from India, customs documentation",
  },
  "/business-shipping": {
    title: "Business Shipping Solutions for Indian Companies | ShipAny",
    description: "Centralize courier selection, pickups, labels, tracking, COD, returns and logistics reporting for growing Indian businesses.",
    keywords: "business shipping India, logistics platform for businesses, ecommerce logistics India",
  },
  "/integrations": {
    title: "Shipping Integrations for Shopify, WooCommerce & APIs | ShipAny",
    description: "Connect ShipAny with ecommerce stores, marketplaces, ERPs and custom systems for automated shipping, tracking and webhook workflows.",
    keywords: "shipping integrations India, Shopify shipping integration, WooCommerce shipping, logistics API India",
  },
  "/developers": {
    title: "ShipAny Shipping API for Developers | Rates, Labels, Tracking",
    description: "Integrate domestic and international shipping APIs for rates, shipment creation, labels, tracking, cancellations, NDR, returns and signed webhooks.",
    keywords: "shipping API India, courier API, logistics API, shipment tracking API, shipping aggregator API",
  },
  "/track-shipment": {
    title: "Track Shipment Online — Domestic & International | ShipAny",
    description: "Track ShipAny domestic and international shipments with one unified timeline from pickup through delivery.",
    keywords: "track shipment India, courier tracking, international shipment tracking",
  },
  "/about": {
    title: "About ShipAny — Indian Logistics Technology Platform",
    description: "Learn how ShipAny helps Indian businesses ship smarter with courier aggregation, logistics automation, tracking and shipping APIs.",
    keywords: "about ShipAny, Indian logistics technology, shipping platform India",
  },
  "/faq": {
    title: "Shipping, COD, International & API FAQs | ShipAny Help Center",
    description: "Find answers about ShipAny shipment booking, courier rates, wallet billing, COD, tracking, NDR, international shipping and developer APIs.",
    keywords: "shipping FAQ India, courier FAQ, COD shipping questions, international shipping help",
  },
  "/contact": {
    title: "Contact ShipAny — Shipping Support & Sales",
    description: "Contact ShipAny for shipping support, business enquiries, API access and logistics platform assistance.",
    keywords: "contact ShipAny, shipping support India, logistics sales contact",
  },
  "/blog": {
    title: "Shipping & Logistics Guides for Indian Businesses | ShipAny Blog",
    description: "Read practical shipping, courier, COD, NDR, returns and international logistics guides for Indian sellers and exporters.",
    keywords: "shipping blog India, logistics guides, ecommerce shipping tips",
  },
  "/signup": {
    title: "Create Your Free ShipAny Account | Start Shipping",
    description: "Create a ShipAny account to compare couriers, book domestic and international shipments, print labels and track deliveries.",
    keywords: "shipping platform signup, courier account India, ShipAny registration",
  },
  "/terms": {
    title: "Terms & Conditions | ShipAny",
    description: "Read the terms and conditions governing use of ShipAny's shipping platform and services.",
    keywords: "ShipAny terms, shipping terms and conditions",
  },
  "/privacy": {
    title: "Privacy Policy | ShipAny",
    description: "Learn how ShipAny collects, uses, protects and retains customer, shipment and account data.",
    keywords: "ShipAny privacy policy, logistics data privacy",
  },
  "/refund-policy": {
    title: "Refund & Cancellation Policy | ShipAny",
    description: "Understand ShipAny shipment cancellation, wallet refund and billing adjustment policies.",
    keywords: "ShipAny refund policy, shipment cancellation refund",
  },
  "/shipping-policy": {
    title: "Shipping Policy & Prohibited Items | ShipAny",
    description: "Review ShipAny shipping service terms, prohibited items, packaging responsibilities and courier delivery policies.",
    keywords: "shipping policy India, prohibited courier items, packaging policy",
  },
  "/cookie-policy": {
    title: "Cookie Policy | ShipAny",
    description: "Learn how ShipAny uses cookies and similar technologies across its website and customer platform.",
    keywords: "ShipAny cookie policy",
  },
  "/grievance": {
    title: "Grievance Officer & Escalations | ShipAny",
    description: "Find ShipAny grievance and escalation contact information for unresolved support concerns.",
    keywords: "ShipAny grievance officer, shipping support escalation",
  },
};

const DEFAULT_SEO = PAGE_SEO["/"];
PAGE_SEO["/prohibited-items"] = {
  title: "Prohibited & Restricted Items — Shipping Guidelines | ShipAny",
  description: "Know what you can and cannot send before booking — complete list of prohibited and restricted items for domestic and international courier shipments from India.",
  keywords: "prohibited items courier India, restricted items shipping, what cannot be couriered, international shipping prohibited items list, courier guidelines India",
};
const NOINDEX_PREFIXES = ["/app", "/admin", "/ai-admin", "/franchise", "/console"];
const NOINDEX_EXACT = new Set(["/login", "/forgot-password", "/set-password", "/status", "/track"]);

function upsertMeta(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const isPrivate = NOINDEX_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
    const isTrackingResult = pathname.startsWith("/track/") && pathname.length > "/track/".length;
    const known = Boolean(PAGE_SEO[pathname]);
    const page = PAGE_SEO[pathname] || DEFAULT_SEO;
    const noindex = isPrivate || isTrackingResult || NOINDEX_EXACT.has(pathname) || !known;
    const title = isPrivate ? "ShipAny — Secure Business Dashboard" : page.title;
    const description = isPrivate
      ? "Secure ShipAny business dashboard for authorized users."
      : page.description;
    const cleanPath = isTrackingResult ? "/track" : pathname.replace(/\/+$/, "") || "/";
    const canonical = isPrivate ? `${SITE_URL}/` : `${SITE_URL}${cleanPath}`;

    document.documentElement.lang = "en-IN";
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", isPrivate ? "ShipAny dashboard" : page.keywords);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "ShipAny");
    upsertMeta("property", "og:locale", "en_IN");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:image:alt", "ShipAny domestic and international shipping platform");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", OG_IMAGE);
    upsertCanonical(canonical);
  }, [pathname]);
  return null;
}

export function JsonLd({ data }) {
  return <script type="application/ld+json">{JSON.stringify(data)}</script>;
}
